import React, { useCallback, useContext } from 'react'
import { useEffect, useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { Button } from '@mui/material'
import { GridColDef } from '@mui/x-data-grid'

import { ApiServiceContext } from '../../api/ApiService.context'
import { Organization, OrganizationRow } from '../../api/organization/types'
import { AppPage } from '../../components/Page/Page'
import { AppTableWithButtons, RowStatus } from '../../components/TableWithButtons/TableWithButtons'
import { Attribution } from '../../api/attribution/types'
import { SortConfig, DEFAULT_SORT } from '../../utils/sortUtils'
import { APIQuery } from '../../utils/sharedTypes'
import { ApiServiceCollection } from '../../api/ApiServiceCollection.class'
import { AttributionService } from '../../api/attribution/AttributionService.class'
import { OrganizationService } from '../../api/organization/OrganizationService.class'
import { TableRetryButton } from '../../components/TableWithButtons/TableRetryButton'

const columns: GridColDef<OrganizationRow>[] = [
    {
        field: 'displayName',
        headerName: 'Name',
        width: 300,
        minWidth: 100,
        editable: false,
    },
    {
        field: 'status',
        headerName: 'Status',
        width: 100,
        editable: false,
    },
    {
        field: 'hasLink',
        headerName: 'Link',
        width: 70,
        minWidth: 100,
        editable: false,
        type: 'boolean',
        valueGetter: (value, row) => row.permissions?.link,
    },
    {
        field: 'hasApi',
        headerName: 'API',
        width: 70,
        minWidth: 100,
        editable: false,
        type: 'boolean',
        valueGetter: (value, row) => row.permissions?.api,
    },
    {
        field: 'directToVat',
        headerName: 'Direct to VAT',
        description: 'Direct to VAT',
        width: 100,
        minWidth: 150,
        editable: false,
        type: 'boolean',
        valueGetter: (value, row) => row.permissions?.directToVat,
    },
    {
        field: 'editLink',
        headerName: 'Edit',
        width: 100,
        editable: false,
        renderCell: (cellValues) => {
            return <Link to={`/organization/${cellValues.row.organizationGuid}`}>Edit</Link>;
        }
    },
];

const attributionColumns: GridColDef<Attribution>[] = [
    {
        field: 'displayName',
        headerName: 'Name',
        width: 300,
        minWidth: 100,
        editable: false,
    },
    {
        field: 'status',
        headerName: 'Status',
        width: 150,
        editable: false,
    },
    {
        field: 'type',
        headerName: 'Type',
        width: 150,
        editable: false,
    },
    {
        field: 'editLink',
        headerName: 'Edit',
        width: 100,
        editable: false,
        renderCell: (cellValues) => {
            return <Link to={`/attribution/${cellValues.row.attributionGuid}`}>Edit</Link>;
        }
    },
];

type DashboardLoadingState = 'NOT STARTED' | 'LOADING' | 'SUCCESS' | 'FAIL';

export const DashboardPage = () => {
    const apiServiceContext = useContext(ApiServiceContext);
    const navigate = useNavigate();
    const {
        AttributionService,
        OrganizationService
    } = apiServiceContext.getApiServiceCollections() as {
        AttributionService: AttributionService;
        OrganizationService: OrganizationService;
        [key: string]: ApiServiceCollection;
    };

    const [organizations, setOrganizations] = useState<OrganizationRow[]>([]);
    const [organizationErrorMessage, setOrganizationErrorMessage] = useState("");
    const [organizationTotalRecords, setOrganizationTotalRecords] = useState(0);
    const [organizationLoadingState, setOrganizationLoadingState] = useState<DashboardLoadingState>('NOT STARTED');
    const [organizationPaginationModel, setOrganizationPaginationModel] = useState({ page: 0, pageSize: 10 });

    const [attributions, setAttributions] = useState<Attribution[]>([]);
    const [attributionErrorMessage, setAttributionErrorMessage] = useState("");
    const [attributionTotalRecords, setAttributionTotalRecords] = useState(0);
    const [attributionLoadingState, setAttributionLoadingState] = useState<DashboardLoadingState>('NOT STARTED');
    const [attributionPaginationModel, setAttributionPaginationModel] = useState({ page: 0, pageSize: 10 });

    const [orgStatusToggle, setOrgStatusToggle] = useState<RowStatus>('ACTIVE');
    const [attrStatusToggle, setAttrStatusToggle] = useState<RowStatus>('ACTIVE');

    const [orgSort, setOrgSort] = useState<SortConfig | null>(DEFAULT_SORT);
    const [attrSort, setAttrSort] = useState<SortConfig | null>(DEFAULT_SORT);
    const [orgDisplayNameFilter, setOrgDisplayNameFilter] = useState<string>('');
    const [attrDisplayNameFilter, setAttrDisplayNameFilter] = useState<string>('');

    const getOrganizations = useCallback(async (page: number, pageSize: number, query?: APIQuery) => {
        setOrganizationErrorMessage('');
        setOrganizationLoadingState('LOADING');
        const getOrganizationsEndpoint = OrganizationService.getOrganizations;
        let getOrgsResp = await getOrganizationsEndpoint({ skip: page * pageSize, limit: pageSize, query });
        let attempts = 0;
        while (getOrgsResp.retry) {
            // TODO - Display retries - put retry in state
            getOrgsResp = await getOrgsResp.retry.catch(error => error);
            setOrganizationErrorMessage(`Attempt ${attempts}`);
            attempts += 1;
            console.log(`Completed retry attempt ${attempts} for getOrganizations`)
        }
        if (getOrgsResp.error) {
            setOrganizationErrorMessage('Failed to load Organizations');
            setOrganizationLoadingState('FAIL');
            return;
        }

        setOrganizationLoadingState('SUCCESS');
        setOrganizations(getOrgsResp.jsonBody!.data.map((row: Organization) => ({
            ...row,
            permissions: {
                api: true,
                link: true,
                directToVat: true
            }
        })) as OrganizationRow[]);
        setOrganizationTotalRecords(getOrgsResp.jsonBody!.total);
    }, [OrganizationService]);

    const getAttributions = useCallback(async (page: number, pageSize: number, query?: APIQuery) => {
        setAttributionErrorMessage('');
        const getAttributionEndpoint = AttributionService.getAttributions;
        setAttributionLoadingState('LOADING');

        let getAttrsResp = await getAttributionEndpoint({ skip: page * pageSize, limit: pageSize, query });
        let attempts = 0;
        while (getAttrsResp.retry) {
            // TODO - Display retries - put retry in state
            getAttrsResp = await getAttrsResp.retry.catch(error => error);
            attempts += 1;
            console.log(`Completed retry attempt ${attempts} for getAttributions`);
        }
        if (getAttrsResp.error) {
            setAttributionErrorMessage('Failed to load Attribution');
            setAttributionLoadingState('FAIL');
            return;
        }
        setAttributionLoadingState('SUCCESS');
        setAttributions(getAttrsResp.jsonBody!.data as Attribution[]);
        setAttributionTotalRecords(getAttrsResp.jsonBody!.total);
    }, [AttributionService]);

    useEffect(() => {
        if (organizationLoadingState !== 'NOT STARTED') {
            return;
        }
        setOrganizationLoadingState('LOADING');
        getOrganizations(organizationPaginationModel.page, organizationPaginationModel.pageSize);
    }, [getOrganizations, organizationLoadingState, organizationPaginationModel.page, organizationPaginationModel.pageSize]);

    useEffect(() => {
        if (attributionLoadingState !== 'NOT STARTED') {
            return;
        }
        setAttributionLoadingState('LOADING');
        getAttributions(attributionPaginationModel.page, attributionPaginationModel.pageSize);
    }, [getAttributions, attributionLoadingState, attributionPaginationModel.page, attributionPaginationModel.pageSize]);

    const handleOrganizationPageChange = useCallback((page: number, pageSize: number) => {
        setOrganizationPaginationModel({ page, pageSize });
        getOrganizations(page, pageSize, {
            filter: {
                displayName: orgDisplayNameFilter
            },
            status: orgStatusToggle,
            sort: orgSort || undefined
        });
    }, [getOrganizations, orgStatusToggle, orgDisplayNameFilter, orgSort]);

    const handleAttributionPageChange = useCallback((page: number, pageSize: number) => {
        setAttributionPaginationModel({ page, pageSize });
        getAttributions(page, pageSize, {
            filter: {
                displayName: attrDisplayNameFilter
            },
            status: attrStatusToggle,
            sort: attrSort || undefined
        });
    }, [getAttributions, attrStatusToggle, attrDisplayNameFilter, attrSort]);

    const handleOrganizationStatusChange = useCallback((rowStatus: RowStatus) => {
        setOrgStatusToggle(rowStatus);
        setOrganizationPaginationModel({ page: 0, pageSize: 10 });
        getOrganizations(0, 10, {
            filter: {
                displayName: orgDisplayNameFilter
            },
            status: rowStatus,
            sort: orgSort || undefined
        });
    }, [getOrganizations, orgDisplayNameFilter, orgSort]);

    const handleAttributionStatusChange = useCallback((rowStatus: RowStatus) => {
        setAttrStatusToggle(rowStatus);
        setAttributionPaginationModel({ page: 0, pageSize: 10 });
        getAttributions(0, 10, {
            filter: {
                displayName: attrDisplayNameFilter
            },
            status: rowStatus,
            sort: attrSort || undefined
        });
    }, [getAttributions, attrDisplayNameFilter, attrSort]);

    const handleOrganizationSearch = useCallback((searchTerm: string) => {
        setOrgDisplayNameFilter(searchTerm);
        setOrganizationPaginationModel({ page: 0, pageSize: 10 });
        getOrganizations(0, 10, {
            filter: {
                displayName: searchTerm
            },
            status: orgStatusToggle,
            sort: orgSort || undefined
        });
    }, [getOrganizations, orgStatusToggle, orgSort]);

    const handleAttributionSearch = useCallback((searchTerm: string) => {
        setAttrDisplayNameFilter(searchTerm);
        setAttributionPaginationModel({ page: 0, pageSize: 10 });
        getAttributions(0, 10, {
            filter: {
                displayName: searchTerm
            },
            status: attrStatusToggle,
            sort: attrSort || undefined
        });
    }, [getAttributions, attrStatusToggle, attrSort]);

    const handleOrganizationSortChange = useCallback((sort: SortConfig | null) => {
        const effectiveSort = sort || DEFAULT_SORT;
        setOrgSort(effectiveSort);
        setOrganizationPaginationModel({ page: 0, pageSize: 10 });
        getOrganizations(0, 10, {
            filter: {
                displayName: orgDisplayNameFilter
            },
            status: orgStatusToggle,
            sort: effectiveSort || undefined
        });
    }, [getOrganizations, orgStatusToggle, orgDisplayNameFilter]);

    const handleAttributionSortChange = useCallback((sort: SortConfig | null) => {
        const effectiveSort = sort || DEFAULT_SORT;
        setAttrSort(effectiveSort);
        setAttributionPaginationModel({ page: 0, pageSize: 10 });
        getAttributions(0, 10, {
            filter: {
                displayName: attrDisplayNameFilter
            },
            status: attrStatusToggle,
            sort: effectiveSort || undefined
        });
    }, [getAttributions, attrStatusToggle, attrDisplayNameFilter]);

    return (
        <AppPage id="dashboard-page" >
            <div className='dashboard-section' >
                <AppTableWithButtons
                    rowIdField='organizationGuid'
                    rows={organizations}
                    columns={columns}
                    title="Organizations"
                    pagination
                    totalRecords={organizationTotalRecords}
                    isLoading={organizationLoadingState === 'LOADING'}
                    onPageChange={handleOrganizationPageChange}
                    showStatusToggle={true}
                    rowStatus={orgStatusToggle}
                    onStatusChange={(rowStatus: RowStatus) => {
                        handleOrganizationStatusChange(rowStatus);
                    }}
                    defaultSort={DEFAULT_SORT}
                    onSortChange={handleOrganizationSortChange}
                    showSearch
                    onSearch={handleOrganizationSearch}
                    slots={{
                        noRowsOverlay: TableRetryButton({
                            message: organizationErrorMessage,
                            retry: () => setOrganizationLoadingState('NOT STARTED')
                        })
                    }}
                >
                    <Button variant='outlined' onClick={() => navigate('/organization')}>Add Organization</Button>
                </AppTableWithButtons>
            </div>
            <div className='dashboard-section' >
                <AppTableWithButtons
                    rowIdField='attributionGuid'
                    rows={attributions}
                    columns={attributionColumns}
                    title="Attributions"
                    pagination
                    totalRecords={attributionTotalRecords}
                    isLoading={attributionLoadingState === 'LOADING'}
                    onPageChange={handleAttributionPageChange}
                    showStatusToggle={true}
                    rowStatus={attrStatusToggle}
                    onStatusChange={(rowStatus: RowStatus) => {
                        handleAttributionStatusChange(rowStatus);
                    }}
                    defaultSort={DEFAULT_SORT}
                    onSortChange={handleAttributionSortChange}
                    showSearch
                    onSearch={handleAttributionSearch}
                    slots={{
                        noRowsOverlay: TableRetryButton({
                            message: attributionErrorMessage,
                            retry: () => setAttributionLoadingState('NOT STARTED')
                        })
                    }}
                >
                    <Button variant='outlined' onClick={() => navigate('/attribution')}>Add Attribution</Button>
                </AppTableWithButtons>
            </div>
        </AppPage>
    )
};
